import React, { useCallback, useEffect, useState } from 'react'
import { useLazyGetAdditionalObjectsQuery } from '../../../../../../store/integrations/integrations'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { type User } from '../../../../../../models/User'
import {
  type CRMWorkflow,
  type CRMWorkflowTemplate,
  type RedtailWorkflowTemplate,
  type WealthboxAdditionalObjects,
  type WealthboxWorkflow,
} from '../../../../../../models/Integration'
import { Accordion, Form, Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import '../UpdateCRMContactModal.scss'
import { addToast } from '../../../../../../store/toast/toastSlice'

import { Button } from '../../../../../../components/Button/Button'
import { ZocksAIAssistantTag } from '../../../../../../components/Tag/Tag'
import { CRMWorkflowTemplateView } from './CRMWorkflowTemplateView'
import { CRMWorkflowView } from './CRMWorkflowView'
import { SuggestedWorkflows } from './SuggestedWorkflows'

export interface UpdateCRMWorkflowProps {
  roomId: string
  sessionId: string
  serviceId: string
  contactId: string
  contact: User
  isSelectedExternalContact: boolean
}

type FetchStatus = 'loading' | 'ready' | 'error'
type WorkflowsStatus = FetchStatus | 'unlinked'

export const UpdateCRMWorkflow: React.FC<UpdateCRMWorkflowProps> = ({ roomId, sessionId, serviceId, contactId, contact, isSelectedExternalContact }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [triggerGetAdditionalObjects, { isFetching }] = useLazyGetAdditionalObjectsQuery()
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('loading')
  const [runningWorkflows, setRunningWorkflows] = useState<CRMWorkflow[]>([])
  const [workflowTemplates, setWorkflowTemplates] = useState<CRMWorkflowTemplate[]>([])
  const [searchNewWorkflow, setSearchNewWorkflow] = useState<string>('')
  const [activeKey, setActiveKey] = useState<string>('runningWorkflows')
  const [animateRunningWorkflowBackground, setAnimateRunningWorkflowBackground] = useState<boolean>(false)
  const [animateWorkflowStepBackground, setAnimateWorkflowStepBackground] = useState<number | null>(null)
  const externalId = contact.integrations?.find((i) => i.serviceId === serviceId)?.externalId

  const status: WorkflowsStatus = !externalId ? 'unlinked' : isFetching ? 'loading' : fetchStatus

  const loadWorkflows = useCallback(() => {
    if (!externalId) {
      return
    }
    triggerGetAdditionalObjects({
      accountId,
      connector: serviceId,
      relatedTo: { id: externalId },
      includeWorkflows: true,
      includeWorkflowTemplates: true,
    })
      .unwrap()
      .then((response) => {
        const additionalObjects = response as WealthboxAdditionalObjects
        setRunningWorkflows(additionalObjects.workflows ?? [])
        setWorkflowTemplates(additionalObjects.workflowTemplates ?? [])
        setFetchStatus('ready')
      })
      .catch(() => {
        setFetchStatus('error')
        dispatch(addToast({ variant: 'error', title: 'Could not load workflows', messages: ['Please try again.'] }))
      })
  }, [accountId, dispatch, externalId, serviceId, triggerGetAdditionalObjects])

  useEffect(() => {
    loadWorkflows()
  }, [loadWorkflows])

  const handleWorkflowStarted = (workflow: CRMWorkflow) => {
    setRunningWorkflows([...runningWorkflows, workflow])
    setAnimateRunningWorkflowBackground(true)
  }

  const handleWorkflowUpdated = (workflow: CRMWorkflow) => {
    const workflows = runningWorkflows
      .map((runningWorkflow) => {
        if (runningWorkflow.id !== workflow.id) {
          return runningWorkflow
        }
        if (workflow.activeStep) {
          return workflow
        }
        dispatch(addToast({ variant: 'success', title: 'Success', messages: ['The workflow has been completed.'] }))
        return null
      })
      .filter((w): w is WealthboxWorkflow => !!w)
    setRunningWorkflows(workflows)
    setAnimateWorkflowStepBackground(workflow.id)
  }

  if (status === 'loading') {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (status !== 'ready') {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center gap-2 p-4 paragraph-small zocks-text-gray-600'>
        <div>
          {status === 'unlinked'
            ? 'This contact is not linked to your CRM yet, so there are no workflows to show.'
            : 'We could not load workflows from your CRM.'}
        </div>
        {status === 'error' ? (
          <Button variant='secondary' className='button-small' onClick={loadWorkflows} disabled={isFetching}>
            Try again
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <>
      <Accordion defaultActiveKey='runningWorkflows' activeKey={activeKey} onSelect={(e) => setActiveKey(`${e}`)}>
        <Accordion.Item eventKey='suggestedWorkflows'>
          <Accordion.Header>
            <div className='d-flex gap-2'>
              Workflow suggestions <ZocksAIAssistantTag />
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <div className='d-flex flex-column gap-4'>
              <SuggestedWorkflows
                roomId={roomId}
                sessionId={sessionId}
                serviceId={serviceId}
                contactId={contactId}
                contact={contact}
                isSelectedExternalContact={isSelectedExternalContact}
                runningWorkflows={runningWorkflows}
                workflowTemplates={workflowTemplates}
                onWorkflowUpdated={handleWorkflowUpdated}
                onWorkflowStarted={handleWorkflowStarted}
              />
            </div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item
          eventKey='runningWorkflows'
          className={classNames({ 'accordion-background-animation': animateRunningWorkflowBackground })}
          onAnimationEnd={() => setAnimateRunningWorkflowBackground(false)}
        >
          <Accordion.Header>Running workflows</Accordion.Header>
          <Accordion.Body>
            <div className='d-flex flex-column gap-4'>
              {runningWorkflows.length
                ? runningWorkflows.map((workflow) => (
                    <div
                      key={workflow.id}
                      className={classNames({ 'workflow-step-background-animation': animateWorkflowStepBackground === workflow.id })}
                      onAnimationEnd={() => setAnimateWorkflowStepBackground(null)}
                    >
                      <CRMWorkflowView workflow={workflow} serviceId={serviceId} contact={contact} onWorkflowStepCompleted={handleWorkflowUpdated} />
                    </div>
                  ))
                : 'No running workflow.'}
            </div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey='newWorkflows'>
          <Accordion.Header>Start new workflow</Accordion.Header>
          <Accordion.Body>
            <div className='d-flex flex-column gap-3'>
              <Form.Control
                id='searchNewWorkflowInput'
                value={searchNewWorkflow}
                onChange={(e) => setSearchNewWorkflow(e.currentTarget.value)}
                placeholder='Search in workflow templates'
              />
              <div className='workflow-accordion-body-scroll d-flex flex-column gap-4'>
                {workflowTemplates.length
                  ? workflowTemplates
                      .filter((workflowTemplate) => workflowTemplate.name?.toLowerCase().includes(searchNewWorkflow.toLowerCase()))
                      .map((workflowTemplate) => {
                        return (
                          <CRMWorkflowTemplateView
                            key={serviceId === 'redtail' ? (workflowTemplate as RedtailWorkflowTemplate).redtailOneId : workflowTemplate.id}
                            workflowTemplate={workflowTemplate}
                            serviceId={serviceId}
                            contact={contact}
                            onWorkflowStarted={handleWorkflowStarted}
                          />
                        )
                      })
                  : "You don't have any workflow templates"}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </>
  )
}
